import dayjs from './date.js';

function escapeRegExp(value) {
	return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function activityMatchesSearchText(activity, filterText) {
	if (!filterText) {
		return true;
	}
	const needle = String(filterText);
	let matcher = null;
	try {
		matcher = new RegExp(escapeRegExp(needle), 'i');
	} catch {
		matcher = null;
	}
	const name = activity.name ? String(activity.name) : '';
	const activityId = activity.activityId != null ? String(activity.activityId) : '';
	if (matcher) {
		return matcher.test(name) || matcher.test(activityId);
	}
	const lower = needle.toLowerCase();
	return name.toLowerCase().indexOf(lower) !== -1
		|| activityId.toLowerCase().indexOf(lower) !== -1;
}

function filterProjectActivities(state) {
	const { currentDataset, datasets, projectActivities, projectFilter } = state;
	const datasetInfo = currentDataset && datasets ? datasets[currentDataset] : null;

	if (!datasetInfo || !Array.isArray(projectActivities)) {
		return {
			filteredActivities: null,
			filteredActivitiesMap: null
		};
	}

	if (!projectFilter) {
		return {
			filteredActivities: projectActivities,
			filteredActivitiesMap: projectActivities.reduce((activitiesMap, activity, index) => {
				activitiesMap[activity.activityId] = index;
				return activitiesMap;
			})
		};
	}

	const hrsPerDay = datasetInfo.HoursPerDay || 1;
	const filteredActivitiesMap = {};
	const flagFilter = projectFilter.flags && typeof projectFilter.flags === 'object' ? projectFilter.flags : {};
	const statusFilter = projectFilter.activityStatus && typeof projectFilter.activityStatus === 'object' ? projectFilter.activityStatus : {};
	const resourceFilter = projectFilter.resources && typeof projectFilter.resources === 'object' ? projectFilter.resources : {};
	const codeFilter = projectFilter.activityCodes && typeof projectFilter.activityCodes === 'object' ? projectFilter.activityCodes : {};
	const hasFlagFilter = Object.keys(flagFilter).length !== 0;
	const filterText = projectFilter.searchText;
	const hasStatusFilter = Object.keys(statusFilter).length !== 0;
	const hasResourceFilter = Object.keys(resourceFilter).length !== 0;
	const applyFloatFilter = projectFilter.applyFloatFilter;
	const filterHasPhotos = projectFilter.hasPhotos;
	const filterChanges = projectFilter.changes;

	let filterHasCodes = false;
	for (const i in codeFilter) {
		if (codeFilter[i] && Object.keys(codeFilter[i]).length) {
			filterHasCodes = true;
			break;
		}
	}

	let startDate = projectFilter.startDate;
	let endDate = projectFilter.endDate;

	if (projectFilter.useDynamicDates) {
		if (projectFilter.dynamicRange) {
			if (projectFilter.dynamicRange === 'Today') {
				startDate = dayjs().startOf('day').valueOf();
				endDate = dayjs().endOf('day').valueOf();
			} else if (projectFilter.dynamicRange === 'Next 2 Days') {
				startDate = dayjs().startOf('day').valueOf();
				endDate = dayjs().add(2, 'days').endOf('day').valueOf();
			} else 	if (projectFilter.dynamicRange === 'Current Week') {
				// Monday–Sunday (HITO weekStart).
				startDate = dayjs().startOf('week').valueOf();
				endDate = dayjs().endOf('week').valueOf();
			} else if (projectFilter.dynamicRange === 'Next Week') {
				startDate = dayjs().add(7, 'days').startOf('week').valueOf();
				endDate = dayjs().add(7, 'days').endOf('week').valueOf();
			} else if (projectFilter.dynamicRange === 'Next 2 Weeks') {
				startDate = dayjs().add(7, 'days').startOf('week').valueOf();
				endDate = dayjs().add(14, 'days').endOf('week').valueOf();
			} else if (projectFilter.dynamicRange === 'Next 3 Weeks') {
				startDate = dayjs().add(7, 'days').startOf('week').valueOf();
				endDate = dayjs().add(21, 'days').endOf('week').valueOf();
			} else if (projectFilter.dynamicRange === 'Current Month') {
				startDate = dayjs().startOf('month').valueOf();
				endDate = dayjs().endOf('month').valueOf();
			} else if (projectFilter.dynamicRange === 'Next Month') {
				startDate = dayjs().add(1, 'months').startOf('month').valueOf();
				endDate = dayjs().add(1, 'months').endOf('month').valueOf();
			}
		}
	}

	let index = 0;
	const filteredActivities = projectActivities.filter((activity) => {
		if (!activity) {
			return false;
		}
		//if activity statusCode doesn't match one of the filters, filter out
		if (hasFlagFilter) {
			const flagKey = activity.flag == null || typeof activity.flag === 'object' ? '' : activity.flag;
			if (!(flagKey in flagFilter)) {
				return false;
			}
		}

		// Activity ID or name must match filter text
		if (filterText && !activityMatchesSearchText(activity, filterText)) {
			return false;
		}

		//if activity statusCode doesn't match one of the filters, filter out
		if (hasStatusFilter) {
			if (!(activity.statusCode in statusFilter)) {
				return false;
			}
		}

		// Activity codes: OR within a type (hashmap membership), AND across types.
		// Iterate selected filter types (not activity.properties) so a missing type fails AND.
		if (filterHasCodes) {
			for (const activityCodeTypeId in codeFilter) {
				const selectedCodes = codeFilter[activityCodeTypeId];
				if (!selectedCodes || !Object.keys(selectedCodes).length) {
					continue;
				}
				const activityCode = activity.properties && activity.properties['_' + activityCodeTypeId];
				if (!activityCode || !(activityCode.id in selectedCodes)) {
					return false;
				}
			}
		}

		//if activity finishes before the filter start or starts after the filter ends, filter out (never filter TK_Active when dynamic date is next 2 or 3 weeks)
		if (!(projectFilter.useDynamicDates && projectFilter.dynamicRange && (projectFilter.dynamicRange === 'Next 2 Weeks' || projectFilter.dynamicRange === 'Next 3 Weeks') && activity.statusCode === 'TK_Active')) {
			if (startDate && activity.finish < startDate) {
				return false;
			}
			if (endDate && activity.start > endDate) {
				return false;
			}
		}

		//if float filter is enabled and status is TK_Complete or total float is outside the filter range, filter out
		if (applyFloatFilter && (activity.statusCode === 'TK_Complete' || activity.totalFloat / hrsPerDay < projectFilter.floatMin || activity.totalFloat / hrsPerDay > projectFilter.floatMax)) {
			return false;
		}

		//if activity doesn't have any resources in the resource filter, filter out
		if (hasResourceFilter) {
			let resourceMatch = false;
			const activityResources = Array.isArray(activity.resources) ? activity.resources : [];
			for (let i = 0; i < activityResources.length; i++) {
				if (activityResources[i] in resourceFilter) {
					resourceMatch = true;
					break;
				}
			}
			if (!resourceMatch) {
				return false;
			}
		}

		//if activity has no photos, filter out
		if (filterHasPhotos) {
			const images = activity.images && typeof activity.images === 'object' ? activity.images : {};
			if (Object.keys(images).length === 0 || images.length === 0) {
				return false;
			}
		}

		// If activity has no changes, filter it out
		if (filterChanges) {
			if (!activity.hasChanged) {
				return false;
			}
		}

		//if activity hasn't been filtered out, add it to the hash map
		filteredActivitiesMap[activity.activityId] = index++;
		return true;
	});

	return {
		filteredActivities,
		filteredActivitiesMap
	};
}
function filterWBSData(wbsData, activitiesMap) {
	if (!wbsData) {
		return {};
	}
	if (!activitiesMap) {
		return {};
	}
	const filteredWbsData = {};

	for (const w in wbsData) {
		// eslint-disable-next-line no-prototype-builtins
		if (wbsData.hasOwnProperty(w)) {
			const obj = {};
			obj.wbsName = wbsData[w].wbsName;
			obj.wbsId = wbsData[w].wbsId;
			obj.seqNum = wbsData[w].seqNum;
			obj.childrenSequence = wbsData[w].childrenSequence;
			obj.parentWbsId = wbsData[w].parentWbsId;
			const activityIds = Array.isArray(wbsData[w].activityIds) ? wbsData[w].activityIds : [];
			obj.activityIds = activityIds.filter(activityId => {
				return activityId in activitiesMap;
			});
			obj.children = filterWBSData(wbsData[w].children, activitiesMap);
			filteredWbsData[w] = obj;
		}
	}
	return filteredWbsData;
}
function filterActivityCodeTree(activityCodeTree, activitiesMap) {
	if (!activityCodeTree) {
		return {};
	}
	if (!activitiesMap) {
		return {};
	}
	const filteredActivityCodeTree = {};

	for (const codeItem in activityCodeTree) {
		const obj = {};
		obj.name = activityCodeTree[codeItem].name;
		obj.activityCodeId = activityCodeTree[codeItem].activityCodeId;
		obj.activityCodeTypeId = activityCodeTree[codeItem].activityCodeTypeId;
		obj.seqNum = activityCodeTree[codeItem].seqNum;
		obj.childrenSequence = activityCodeTree[codeItem].childrenSequence;
		obj.parentId = activityCodeTree[codeItem].parentId;
		if ('activityIds' in activityCodeTree[codeItem]) {
			obj.activityIds = activityCodeTree[codeItem].activityIds.filter(activityId => {
				return activityId in activitiesMap;
			});
		}
		obj.children = filterActivityCodeTree(activityCodeTree[codeItem].children, activitiesMap);
		filteredActivityCodeTree[codeItem] = obj;
	}
	return filteredActivityCodeTree;
}

function assignDescendantActivityCounts(item) {
	let descendantActivityCount = 'activityIds' in item ? item.activityIds.length : 0;
	for (const c in item.children) {
		descendantActivityCount += assignDescendantActivityCounts(item.children[c]);
	}
	item.descendantActivityCount = descendantActivityCount;
	return descendantActivityCount;
}

/**
 * Flatten a WBS / activity-code tree in the same order the UI renders:
 * node's own activities, then each child in childrenSequence.
 */
function collectTreeActivityIds (item, filteredActivitiesMap, filteredActivities, out) {
	if (!item) {
		return;
	}
	const activityIds = item.activityIds || [];
	for (let i = 0; i < activityIds.length; i++) {
		const activityId = activityIds[i];
		if (activityId in filteredActivitiesMap && filteredActivitiesMap[activityId] in filteredActivities) {
			out.push(activityId);
		}
	}
	const children = item.children || {};
	const childrenSequence = item.childrenSequence || [];
	for (let i = 0; i < childrenSequence.length; i++) {
		const childId = childrenSequence[i];
		if (childId in children) {
			collectTreeActivityIds(
				children[childId],
				filteredActivitiesMap,
				filteredActivities,
				out
			);
		}
	}
}

/**
 * Activity IDs in the order shown in the background list (WBS / Group code tree),
 * otherwise filteredActivities array order.
 */
function getDisplayOrderedActivityIds ({
	projectViewType,
	wbsData,
	projectActivityCodesTree,
	projectGroupBy,
	projectGroupByActivityCode,
	filteredActivities,
	filteredActivitiesMap
}) {
	if (!filteredActivities || !filteredActivities.length || !filteredActivitiesMap) {
		return [];
	}

	if (projectViewType === 'WBS List' && wbsData) {
		const ids = [];
		for (const wbsId in wbsData) {
			collectTreeActivityIds(
				wbsData[wbsId],
				filteredActivitiesMap,
				filteredActivities,
				ids
			);
		}
		return ids;
	}

	if (
		projectViewType === 'Group List' &&
		projectGroupBy === 'Activity Code' &&
		projectGroupByActivityCode &&
		projectActivityCodesTree &&
		projectActivityCodesTree[projectGroupByActivityCode]
	) {
		const tree = projectActivityCodesTree[projectGroupByActivityCode];
		const ids = [];
		const children = tree.children || {};
		const childrenSequence = tree.childrenSequence || [];
		for (let i = 0; i < childrenSequence.length; i++) {
			const childId = childrenSequence[i];
			if (childId in children) {
				collectTreeActivityIds(
					children[childId],
					filteredActivitiesMap,
					filteredActivities,
					ids
				);
			}
		}
		return ids;
	}

	return filteredActivities.map(activity => activity.activityId);
}

export {
	filterProjectActivities,
	filterWBSData,
	filterActivityCodeTree,
	assignDescendantActivityCounts,
	getDisplayOrderedActivityIds
};